import React from 'react';

interface Table2Data {
  title: string;
  company: string;
  headers: string[];
  rows: Array<{
    // Single brand format
    productName?: string;
    stock?: number;
    // Dual brand format (legacy)
    aquafitProductName?: string;
    aquafitStock?: number;
    cablefitProductName?: string;
    cablefitStock?: number;
  }>;
}

interface InventoryTable2Props {
  data: Table2Data;
}

const InventoryTable2: React.FC<InventoryTable2Props> = ({ data }) => {
  // Detect if this is single brand or dual brand format
  const isSingleBrand = data.rows.length > 0 && data.rows[0].productName !== undefined;

  return (
    <div className="bg-white rounded-lg shadow-md overflow-hidden">
      {/* Header */}
      <div className="bg-gradient-to-r from-green-600 to-green-700 px-6 py-4">
        <h2 className="text-xl font-semibold text-white">Table 2: Simple Stock Table</h2>
        <p className="text-green-100 text-sm">{data.title} - {data.company}</p>
      </div>

      {/* Table */}
      <div className="overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              {data.headers.map((header, index) => (
                <th 
                  key={index}
                  className={`px-6 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider ${
                    index === 0 || (index === 2 && !isSingleBrand) ? 'text-left' : 'text-center'
                  }`}
                >
                  {header}
                </th>
              ))}
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {data.rows.map((row, rowIndex) => (
              <tr key={rowIndex} className="hover:bg-gray-50">
                {isSingleBrand ? (
                  // Single brand format
                  <>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      <div className="font-medium">{row.productName}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 text-center">
                      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                        {((row.stock || 0)).toLocaleString()}
                      </span>
                    </td>
                  </>
                ) : (
                  // Dual brand format (legacy)
                  <>
                    {/* AQUAFIT Column */}
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      <div className="font-medium">{row.aquafitProductName}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 text-center">
                      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                        {(row.aquafitStock || 0).toLocaleString()}
                      </span>
                    </td>

                    {/* CABLEFIT Column */}
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      <div className="font-medium">{row.cablefitProductName}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 text-center">
                      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-purple-100 text-purple-800">
                        {(row.cablefitStock || 0).toLocaleString()}
                      </span>
                    </td>
                  </>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Summary */}
      <div className="bg-gray-50 px-6 py-4">
        {isSingleBrand ? (
          <div className="text-center">
            <div className="text-2xl font-bold text-blue-600">
              {data.rows.reduce((sum, row) => sum + (row.stock || 0), 0).toLocaleString()}
            </div>
            <div className="text-sm text-gray-600">Total Stock</div>
          </div>
        ) : (
          <div className="grid grid-cols-2 gap-4">
            <div className="text-center">
              <div className="text-2xl font-bold text-blue-600">
                {data.rows.reduce((sum, row) => sum + (row.aquafitStock || 0), 0).toLocaleString()}
              </div>
              <div className="text-sm text-gray-600">Total AQUAFIT Stock</div>
            </div>
            <div className="text-center">
              <div className="text-2xl font-bold text-purple-600">
                {data.rows.reduce((sum, row) => sum + (row.cablefitStock || 0), 0).toLocaleString()}
              </div>
              <div className="text-sm text-gray-600">Total CABLEFIT Stock</div>
            </div>
          </div>
        )}
      </div>

      {/* Footer */}
      <div className="bg-gray-50 px-6 py-3 border-t">
        <p className="text-xs text-gray-600">
          Simple stock overview - Read-only display
        </p>
      </div>
    </div>
  );
};

export default InventoryTable2;
